import * as Sentry from '@sentry/react';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { readStoredFieldAppSettings } from '@/app/settings/app-settings';
import { normalizeApiBaseUrl } from '@/shared/config/index';

export interface InventoryAxiosResponse<T> {
  data: T;
  status: number;
}

export interface InventoryAxiosClient {
  defaults: {
    baseURL?: string;
  };
  get<T>(url: string, config?: AxiosRequestConfig): Promise<InventoryAxiosResponse<T>>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<InventoryAxiosResponse<T>>;
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<InventoryAxiosResponse<T>>;
}

export function createInventoryAxiosClient(input: { baseUrl?: string; locationApiKey?: string } = {}): AxiosInstance {
  const client = axios.create({
    baseURL: normalizeApiBaseUrl(input.baseUrl ?? readStoredFieldAppSettings().apiBaseUrl),
    headers: {
      Accept: 'application/json',
      ...(input.locationApiKey
        ? {
            Authorization: createLocationApiKeyAuthorizationHeader(input.locationApiKey),
          }
        : {}),
    },
  });

  client.interceptors.response.use(
    response => {
      if (response.status < 200 || response.status >= 300) {
        captureNonOkResponse(response.config, response.status, response.statusText);
      }

      return response;
    },
    error => {
      if (axios.isAxiosError(error) && error.response) {
        captureNonOkResponse(error.config, error.response.status, error.response.statusText, error.code);
      }

      return Promise.reject(error);
    },
  );

  return client;
}

function captureNonOkResponse(
  config: AxiosRequestConfig | undefined,
  status: number,
  statusText: string,
  code?: string,
) {
  // status 0 means the request was aborted or a network-level failure occurred
  // (browser abort, CORS failure, DNS failure). These are not real server errors.
  if (status === 0) return;

  const method = config?.method?.toUpperCase() ?? 'UNKNOWN';
  const path = config?.url?.split(/[?#]/)[0] ?? 'unknown';

  Sentry.captureMessage(`Inventory API ${method} ${path} returned ${status}`, {
    level: 'error',
    tags: {
      'http.method': method,
      'http.status_code': status,
      'url.path': path,
    },
    extra: {
      code,
      statusText,
    },
  });
}

export function createLocationApiKeyAuthorizationHeader(locationApiKey: string) {
  return `Basic ${globalThis.btoa(`InventoryLocation:${locationApiKey}`)}`;
}

export function createLocationApiKeyAxiosClient(input: {
  axiosClient?: InventoryAxiosClient;
  baseUrl?: string;
  locationApiKey: string;
  refreshCache?: boolean;
}): InventoryAxiosClient {
  const axiosClient = input.axiosClient ?? createInventoryAxiosClient({ baseUrl: input.baseUrl });

  return {
    defaults: axiosClient.defaults,
    get: (url, requestConfig) =>
      axiosClient.get(url, withLocationApiKeyAuthorization(requestConfig, input.locationApiKey, input.refreshCache)),
    post: (url, data, requestConfig) =>
      axiosClient.post(
        url,
        data,
        withLocationApiKeyAuthorization(requestConfig, input.locationApiKey, input.refreshCache),
      ),
    put: (url, data, requestConfig) =>
      axiosClient.put(
        url,
        data,
        withLocationApiKeyAuthorization(requestConfig, input.locationApiKey, input.refreshCache),
      ),
  };
}

function withLocationApiKeyAuthorization(
  requestConfig: AxiosRequestConfig | undefined,
  locationApiKey: string,
  refreshCache = false,
) {
  return {
    ...requestConfig,
    headers: {
      ...(requestConfig?.headers as Record<string, unknown> | undefined),
      Authorization: createLocationApiKeyAuthorizationHeader(locationApiKey),
      ...(refreshCache ? { 'Cache-Control': 'no-cache' } : {}),
    },
  };
}
